<template>
  <label
    class="block my-3"
  >
    {{ t(configs.label) }}
  </label>
  <div class="p-fluid flex">
    <BaseInputText
      v-model="inputValue"
      :display-emojis="configs.options.displayEmojis"
      :display-variables="configs.options.displayVariables"
      :variables-list="enrichedVariables"
      :max-length="configs.options.maxLength || null"
      class="w-full"
      :class="{ 'p-invalid': validationError }"
      @update:model-value="handleValueChange"
    />
  </div>
  <InlineMessage
    v-if="validationError"
    severity="error"
    class="mt-2 w-full justify-content-start"
  >
    {{ t(validationError, validationErrorParams) }}
  </InlineMessage>
</template>

<script lang="ts">
import {
  defineComponent, PropType, Ref, ref, watch, onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InlineMessage from 'primevue/inlinemessage';

import { FieldConfig, ParserFieldObject, Property } from '@/types';
import BaseInputText from '@/components/fields/BaseInputText.vue';
import { getVariablesWithCollectionContext, getMetadataVariables } from '@/composables/template-builder/collectionContext';
import { getCustomContactProperties } from '@/configs/custom-contact-properties';

export default defineComponent({
  name: 'InputVariables',
  components: {
    BaseInputText,
    InlineMessage,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const properties: Ref<Array<Property>> = ref(props.parserValues.properties);
    const inputValue: Ref<string> = ref(properties.value[0].value ?? props.configs.options.defaultValue);
    const enrichedVariables = ref(props.configs.options.variablesList || []);
    const validationError: Ref<string | null> = ref(null);
    const validationErrorParams: Ref<Record<string, string | number>> = ref({});

    const validateContent = () => {
      const trimmed = (inputValue.value || '').replace(/<[^>]*>/g, '').trim();

      if (props.configs.options?.required && !trimmed) {
        validationError.value = props.configs.options.requiredErrorKey || null;
        validationErrorParams.value = {};
        return;
      }

      if (props.configs.options?.validator) {
        const result = props.configs.options.validator(inputValue.value);
        validationError.value = result.isValid ? null : (result.errorKey || null);
        validationErrorParams.value = result.errorParams || {};
        return;
      }

      validationError.value = null;
      validationErrorParams.value = {};
    };

    const handleValueChange = () => {
      validateContent();

      const updatedProperties: Array<Property> = properties.value.map((prop: Property) => {
        const updatedProperty = prop;
        updatedProperty.value = inputValue.value;
        return updatedProperty;
      });
      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: updatedProperties,
      });
    };

    watch(() => properties.value[0].value, (values, oldValues) => {
      if (values !== oldValues) {
        inputValue.value = properties.value[0].value;
      }
    }, { immediate: true });

    onMounted(async () => {
      validateContent();

      let baseVariables = props.configs.options.variablesList || [];

      // Add custom contact properties to base variables (for use outside loops)
      try {
        const customSchemas = await getCustomContactProperties();
        const customVariables = customSchemas.flatMap((schema) => schema.properties.map((prop) => ({
          value: `{var=${prop.variableName}}`,
          label: `${schema.schemaName} - ${prop.name}`,
          labelWithBadge: `${schema.schemaName} - ${prop.name} <span class="custom-badge-inline">CUSTOM</span>`,
          type: prop.type,
          isCustom: true,
        })));
        baseVariables = [...baseVariables, ...customVariables];
      } catch (error) {
        console.error('Error loading custom contact properties:', error);
      }

      // Add metadata variables from custom events
      try {
        const metadataVars = await getMetadataVariables();
        baseVariables = [...baseVariables, ...metadataVars];
      } catch (error) {
        console.error('Error loading metadata variables:', error);
      }

      // Enrich variables with collection context
      if (props.parserValues.selector) {
        enrichedVariables.value = await getVariablesWithCollectionContext(
          props.parserValues.selector,
          baseVariables,
        ) as never[];
      } else {
        enrichedVariables.value = baseVariables as never[];
      }
    });

    return {
      t,
      inputValue,
      enrichedVariables,
      validationError,
      validationErrorParams,
      handleValueChange,
    };
  },
});
</script>

<style scoped lang="scss">
.with-emoji {
  & .input-text {
    border-right: none;
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }
}

.with-variables {
  & .input-text {
    border-bottom: none;
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }
}

.input-text {
  font-size: 1.25rem !important;
  padding: 0.625rem 0.625rem !important;
}

:deep() .p-togglebutton.p-button.emoji-button {
  background: $white;
  margin: 0 !important;
  border: solid 1px $solitude;
  border-left: none !important;
  border-radius: 0 0.3rem 0.3rem 0;
  padding: 0 10px;
  width: auto;

  & .p-button-icon-left {
    margin-right: 0;
  }

  & .p-button-label {
    display: none;
  }

  & .p-button-icon {
    color: $heather !important;
  }

  &:hover {
    border-color: $solitude !important;
  }
}

.with-variables:deep() {
  & .emoji-button {
    border-bottom: none !important;
    border-bottom-right-radius: 0 !important;
  }

  & .variable-dropdown {
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;

    & .p-inputtext {
      border-top-left-radius: 0 !important;
      color: $heather;
    }

    & .p-dropdown-trigger {
      border-top-right-radius: 0 !important;
    }
  }
}
</style>
